// ============================================================
// MinikPanel — Giriş noktası
//
// İki açılış yolu var ve ayrımı .env belirliyor:
//
//   firebaseHazir = false → DEMO. Yerel depo, kenar çubuğunda rol
//                            değiştirici. Satış sunumu ve geliştirme.
//   firebaseHazir = true  → GERÇEK. Google/Apple girişi, Firestore,
//                            claim'den gelen rol.
//
// Demo yolunu bilerek koruyoruz: kurulum yapılmamış bir tarayıcıda
// uygulamanın tamamı çalışır hâlde açılıyor ve bu satış için değerli.
// ============================================================

import "./styles.css";

import { PhotoStore } from "./lib/photo.js";
import { faturaGecmisiUret, tahsilatlariKur } from "./domain/invoices.js";
import { yoklamaGecmisiUret } from "./domain/attendance.js";
import { onRerender } from "./lib/rerender.js";
import { dinlemeyeBasla, hydrate } from "./repo/index.js";
import { kapsamiKur, state } from "./state.js";
import { erisilebilirSubeler, girisYap, varsayilanKullanici } from "./domain/auth.js";
import { render, routeFromHash, wireShell } from "./app-shell.js";
import { agiDinle, guncellemeyiDinle } from "./lib/offline.js";
import { gunDegisiminiIzle } from "./lib/gun.js";
import { firebaseHazir } from "./config.js";
import { veriYukle } from "./data/store.js";

// Görünümler render'ı doğrudan çağırmaz; köprüye kaydediyoruz.
onRerender(render);

/**
 * Kabuğu görünür yapar. index.html iskeleti baştan gizli çiziyor;
 * aksi hâlde gerçek girişte panel bir an açılıp giriş ekranına
 * atılıyormuş gibi görünüyor.
 */
const kabugoGoster = () => document.documentElement.classList.remove("kabuk-gizli");

/**
 * Oturum açıldıktan SONRA çalışır — rol, kiracı ve kapsam belli.
 * Sıra önemli: hydrate kapsamı oturumdan okur, fatura üretimi
 * hydrate'ten sonra olmalı (replay yeni öğrenci eklemiş olabilir),
 * sağlık verisi ise mutasyonlardan önce bağlanmalı yoksa kayıtlı
 * ilaç uygulamaları hedef bulamaz.
 */
async function uygulamayiBaslat() {
  // Firestore adaptörü yalnızca kullanıcının eriştiği şubeleri okur;
  // kapsamı oturumdan veriyoruz. Yerel adaptör bunu yok sayar.
  const sonuc = await hydrate({
    tenantId: state.tenantId,
    branchIds: erisilebilirSubeler().map(s => s.id),
  });
  if (sonuc.atlanan) {
    console.warn(`${sonuc.atlanan} eski değişiklik uygulanamadı (veri şeması değişmiş olabilir).`);
  }

  // Sunucunun ürettiği bildirim kayıtları (zamanlanmış işler).
  // Zilde türetilmiş bildirimlerle birleşiyor; okunamazsa uygulama
  // yine açılıyor — bildirim ikincil bilgi.
  const { kayitlariYukle } = await import("./domain/bildirim-kayitlari.js");
  await kayitlariYukle({
    tenantId: state.tenantId,
    branchIds: erisilebilirSubeler().map(s => s.id),
  }).catch(() => {});

  tahsilatlariKur();
  faturaGecmisiUret();
  yoklamaGecmisiUret();
  await PhotoStore.hydrate();

  wireShell();
  routeFromHash();
  render();
  kabugoGoster();

  agiDinle(render);
  guncellemeyiDinle(render);
  gunDegisiminiIzle();

  dinlemeyeBasla(state.tenantId, state.branchId, render);
}

// ------------------------------------------------------------
// Gerçek giriş yolu
// ------------------------------------------------------------
async function gercekGirisleBaslat() {
  const [auth, kapi] = await Promise.all([
    import("./domain/firebase-auth.js"),
    import("./views/giris.js"),
  ]);

  let acildi = false;
  kapi.girisKapisiCiz({ durum: "yukleniyor" });

  const kapiyiCiz = (durumBilgisi) => {
    const kok = kapi.girisKapisiCiz(durumBilgisi);
    kok.onclick = async (e) => {
      const dugme = e.target.closest("[data-auth]");
      if (!dugme) return;
      try {
        if (dugme.dataset.auth === "google") await auth.googleIleGir();
        else if (dugme.dataset.auth === "apple") await auth.appleIleGir();
        else if (dugme.dataset.auth === "hesap-degistir") await auth.hesapDegistir();
        else if (dugme.dataset.auth === "cikis") await auth.cikis();
        else if (dugme.dataset.auth === "yenile") location.reload();
        else if (dugme.dataset.auth === "yeniden-basvur") {
          kapiyiCiz({ ...durumBilgisi, durum: "tanimsiz" });
        }
      } catch (hata) {
        kapi.girisHatasiGoster(cevirHata(hata));
      }
    };

    kok.onsubmit = async (e) => {
      const form = e.target.closest("[data-auth-basvuru]");
      if (!form) return;
      e.preventDefault();
      const veri = Object.fromEntries(new FormData(form));
      try {
        const sonuc = await auth.kresBasvurusuGonder(veri);
        kapiyiCiz({ ...durumBilgisi, durum: sonuc.durum });
      } catch (hata) {
        kapi.girisHatasiGoster(cevirHata(hata));
      }
    };
  };

  auth.oturumuIzle(async (durumBilgisi) => {
    if (durumBilgisi.durum !== "hazir") {
      // Oturum kapandıysa veya yetki gittiyse uygulamayı ayakta
      // bırakmak tehlikeli — ekranda hâlâ veri duruyor olurdu.
      if (acildi) { location.reload(); return; }
      kapiyiCiz(durumBilgisi);
      return;
    }

    const kullanici = auth.claimdenKullanici(durumBilgisi.claim, durumBilgisi.kullanici);

    if (!acildi) {
      // Kiracı ağacı Firestore'dan, kullanıcının kapsamı kadar.
      const { kiraciAgaciniOku, kiracilariOku } = await import("./repo/firestore-veri.js");
      if (kullanici.role === "sistem") {
        veriYukle(await kiracilariOku());
      } else {
        await kiraciAgaciniOku({ tenantId: kullanici.tenantId, branchIds: kullanici.branches });
      }
      kapsamiKur(kullanici.tenantId);
    }

    girisYap(kullanici);

    if (acildi) {
      // Claim değişti (rol, plan, kapsam) — yeniden çiz, baştan kurma.
      render();
      return;
    }

    acildi = true;
    kapi.girisKapisiniKaldir();
    await uygulamayiBaslat();
  });
}

/** Firebase hata kodlarını kullanıcının anlayacağı cümleye çevirir. */
function cevirHata(hata) {
  const kod = hata?.code ?? "";
  if (kod.includes("popup-blocked")) return "Tarayıcı açılır pencereyi engelledi. İzin verip tekrar deneyin.";
  if (kod.includes("operation-not-allowed")) return "Bu giriş yöntemi Firebase'de açık değil.";
  if (kod.includes("network")) return "Bağlantı kurulamadı. İnternetinizi kontrol edin.";
  if (kod.includes("unauthorized-domain")) return "Bu adres Firebase'de yetkili alan adları listesinde değil.";
  if (kod.includes("account-exists")) return "Bu e-posta başka bir yöntemle kayıtlı.";
  return hata?.message ?? "Beklenmeyen bir hata oldu.";
}

// ------------------------------------------------------------
// Demo yolu
// ------------------------------------------------------------
async function demoBaslat() {
  // Tohum veri DİNAMİK import — gerçek modda pakete hiç girmesin.
  // İçinde çocuk adı, alerji ve veli telefonu var; statik import
  // edildiği dönemde bunlar giriş ekranından önce inen paketteydi.
  const [{ TOHUM }, { demoyuBagla }] = await Promise.all([
    import("./data/seed.js"),
    import("./data/demo.js"),
  ]);
  veriYukle(TOHUM.tenants);
  kapsamiKur();
  demoyuBagla();

  // Oturum her şeyden önce açılmalı — menü, yönlendirme ve denetim
  // izi damgası role bağlı.
  girisYap(varsayilanKullanici());
  await uygulamayiBaslat();
}

(firebaseHazir ? gercekGirisleBaslat() : demoBaslat());
